/** App-wide recovery projection. The Worker chooses Shopify reauthorization; existing readiness chooses account linking. */
import { useQuery } from '@tanstack/react-query'
import { shopifyQueryPolicies } from '../queries/shopifyQueryPolicies'
import type { useAppBridge } from '@shopify/app-bridge-react'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { shopifyConnectionRecoveryKey, type ShopifyConnectionRecovery } from '../shopifyQueryClient'
import { requestShopifyNavigation } from '../shopifyNavigation'
import { reportShopifyNavigationFailure } from '../hooks/outside/useShopifyAppNavigation'
import { resolveTestFeedReadinessPresentation } from './TestFeedReadinessCard'
import type { TestFeedConnectionStatus } from '../types'
import { shopifyUiTracker, shopifyUiFailure } from '../shopifyUiTracking'
import { isAbortError } from '../page-state'

export function ShopifyConnectionRecoveryBanner({
  shopify,
  shop,
  readiness,
  onConnect,
  isConnecting,
  onSettings,
  isCurrent,
}: {
  shopify: ReturnType<typeof useAppBridge>
  shop: string
  readiness: TestFeedConnectionStatus | null
  onConnect: () => void
  isConnecting: boolean
  onSettings: () => void
  isCurrent: () => boolean
}) {
  const { data: recovery } = useQuery<ShopifyConnectionRecovery | null>({
    ...shopifyQueryPolicies.studyAuthoring,
    queryKey: shopifyConnectionRecoveryKey(shop),
    initialData: null,
    enabled: false,
  })
  const copy = shopifyComponentCopy.connectionRecovery
  const navigate = (action: 'reconnect' | 'navigate-settings', destination: () => void) => {
    const tracker = shopifyUiTracker(shopify)
    const attempt = tracker.request(action)
    void requestShopifyNavigation(shopify, () => {
      if (isCurrent()) destination()
    })
      .then((accepted) => {
        if (!isCurrent()) tracker.cancelled(attempt, 'scope-change')
        else if (accepted) tracker.completed(attempt)
        else tracker.cancelled(attempt, 'user')
      })
      .catch((cause) => {
        if (isAbortError(cause)) tracker.cancelled(attempt, 'aborted')
        else tracker.failed(attempt, shopifyUiFailure(cause))
        reportShopifyNavigationFailure(shopify, cause)
      })
  }
  if (recovery) {
    const reconnect = () => {
      navigate('reconnect', () => globalThis.window?.open(recovery.reconnectUrl, '_top', 'noopener'))
    }
    return (
      <s-banner heading={copy.shopifyAccess} tone="warning">
        <s-stack direction="block" gap="small-200">
          <s-text>{recovery.merchantMessage}</s-text>
          <s-button onClick={reconnect}>{copy.shopifyAccess}</s-button>
        </s-stack>
      </s-banner>
    )
  }
  if (!readiness || readiness.ready) return null
  const connection = resolveTestFeedReadinessPresentation(readiness, false)
  const needsSettings = readiness.code === 'SHOPIFY_CONNECTION_SELECTION_REQUIRED'
  if (needsSettings) {
    const openSettings = () => {
      navigate('navigate-settings', onSettings)
    }
    return (
      <s-banner heading={copy.reviewConnection} tone="warning">
        <s-stack direction="block" gap="small-200">
          <s-text>{readiness.message}</s-text>
          <s-button onClick={openSettings}>{copy.openSettings}</s-button>
        </s-stack>
      </s-banner>
    )
  }
  if (!connection.canConnect) return null
  return (
    <s-banner heading={connection.title} tone="warning">
      <s-stack direction="block" gap="small-200">
        <s-text>{connection.detail}</s-text>
        <s-button onClick={onConnect} loading={isConnecting}>
          {connection.actionText}
        </s-button>
      </s-stack>
    </s-banner>
  )
}
